import CacheableDataObject from "./CacheableDataObject.jsx";
import moment from "moment";
import { iconForAccount } from "../utilities/accountIcons.js";

export default class PlaidBankAccount extends CacheableDataObject {
	/**
	 * Account types whose balance is money owed rather than money held. Mirrors the same list in
	 * Import_Balance_Converter, which decides the sign a balance is stored with.
	 */
	static LIABILITY_TYPES = ['credit', 'loan'];

	/** An account sent without its balances (as budget/transactions sends them) has none until they arrive. */
	afterConstruct() {
		if(!this.balances) {
			this.balances = [];
		}
	}

	/** The name a person gave the account, or else the bank's names for it. */
	getDisplayName() {
		return this.display_name ?? this.official_name ?? this.name;
	}

	/** The Material icon to draw: the one chosen, or the type's. */
	getIcon() {
		return iconForAccount(this);
	}

	/**
	 * Whether this account's balance counts against what you are worth rather than toward it.
	 *
	 * The type is the whole answer. A credit limit used to stand in for it, and for cards it is a
	 * fine proxy -- but a mortgage never has one, so a loan read as an asset and net worth moved by
	 * twice its principal.
	 */
	isLiability() {
		return PlaidBankAccount.LIABILITY_TYPES.includes(this.type);
	}

	shouldAddToTotalAssets(){
		return this?.user_preferences.add_to_total_assets;
	}

	getLatestBalanceForMonth(selectedDate, useSelectedDateByDay = false) {
		let lastSelectedBalance = null;
		for(let balance of this.balances) {
			if(balance.created_at.isAfter(selectedDate, 'month')) {
				continue;
			}
			if(useSelectedDateByDay && balance.created_at.isAfter(selectedDate, 'day')) {
				continue;
			}

			if(lastSelectedBalance) {
				if(lastSelectedBalance.created_at.isBefore(balance.created_at)) {
					lastSelectedBalance = balance;
				}
			} else {
				lastSelectedBalance = balance;
			}
		}
		return lastSelectedBalance;
	}

	getLatestBalance() {
		if(!this.balances) {
			return null;
		}
		return this.balances[this.balances.length - 1];
	}

	/**
	 * The day this account's newest balance describes, or null when it has none. That is when its data
	 * was last brought up to date, by a Plaid reading or an import alike.
	 *
	 * @returns {moment.Moment|null}
	 */
	getLastUpdated() {
		return this.getLatestBalance()?.created_at ?? null;
	}
}

PlaidBankAccount.prototype.dataLocationParsers = {
	balances: (bankAccount, data) => {
		bankAccount.balances = [];
		for(let balance of data.balances) {
			const created = PlaidBankAccountBalance.create(balance);
			/**
			 * The account, not its type: the two are parsed from the same object and nothing promises
			 * the type arrives first, so the balance asks the account at the moment it needs an answer.
			 */
			created.account = bankAccount;
			bankAccount.balances.push(created);
		}

		bankAccount.balances.sort((a, b) => {
			return a.created_at.isAfter(b.created_at) ? 1 : -1;
		});
	}
};

class PlaidBankAccountBalance extends CacheableDataObject {
	getRelativeBalanceAmount() {
		if(this.isLiability()) {
			return this.current * -1;
		} else {
			return this.current;
		}
	}

	/** Defers to the account, and falls back to the limit for a balance read outside of one. */
	isLiability() {
		if(this.account) {
			return this.account.isLiability();
		}
		return !!this.limit;
	}
}

PlaidBankAccountBalance.prototype.dataLocationParsers = {
	created_at: (balance, data) => {
		balance.created_at = moment.unix(data.created_at);
	},
	current: (balance, data) => {
		balance.current = Math.round(data.current * 100);
	},
	limit: (balance, data) => {
		balance.limit = Math.round(data.limit * 100);
	},
	available: (balance, data) => {
		balance.available = Math.round(data.available * 100);
	}
};
